import React, { useContext, useEffect, useState } from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { I18nContext } from 'i18n'
import styled from 'styled-components'
import { navigate, Link } from 'raviger'
import {
  Organization,
  DetailLoader,
  OrganizationUnavailable,
  ButtonLabel,
  PartnerSpotlight,
  HomeImage,
  Permission,
  MemberInformation,
  CustomLinks,
  DashboardStats,
} from 'components'
import { useOrganization } from 'hooks/useOrganization'
import theme from '../../themes/default'
import { FaSearch } from 'react-icons/fa'
import { BsBarChartFill } from 'react-icons/bs'
import { INDIVIDUAL_ORGANIZATION } from 'utils/constants'
import { getCurrentUser } from 'utils/getCurrentUser'
import { ReviewScholarshipsModal } from '../../molecules/ReviewScholarshipsModal'
import { useUser } from 'hooks/useUser'
import moment from 'moment'
import { checkPermission } from 'utils/permission'
import { reactLocalStorage } from 'reactjs-localstorage'
import Select from 'react-select'
import { useStateCodes } from 'hooks/useStateCodes'
import { useMbrSiteDashboard } from 'hooks/useMbrSiteDashboard'
import { sumBy, groupBy } from 'lodash'

const Landing = () => {
  const { organization, fullName } = getCurrentUser()

  const { translate } = useContext(I18nContext)
  const { dataDetail, loadingDetail, setName, errorDetail } = useOrganization()
  const [showReview, setShowReview] = useState(false)
  const [snoozed, setSnoozed] = useState(false)

  const { data, loading, error, refetch } = useMbrSiteDashboard()

  const { stateList } = useStateCodes()
  const [selectedLocation, setSelectedLocation] = useState({
    value: 'NA',
    label: 'North America',
  })
  // Filter and format location options
  const locationOptions = React.useMemo(() => {
    const usStates =
      stateList
        ?.filter((state) => state.countryCode === 'US')
        ?.sort((a, b) => a.label.localeCompare(b.label))
        ?.map((state) => ({
          value: `${state.countryCode}-${state.isoCode}`,
          label: state.label,
        })) || []

    return [
      { value: 'NA', label: 'North America' },
      { value: 'CA', label: 'Canada' },
      { value: 'US', label: 'United States' },
      {
        label: 'United States',
        options: usStates,
      },
    ]
  }, [stateList])

  const {
    getSnoozedReview,
    dataSnoozedReview,
    loadingSnoozedReview,
    saveSnoozedReview,
    loadingSaveSnoozed,
  } = useUser()

  const handleClick = (target) => {
    if (target) navigate(`/${target}`)
  }

  useEffect(() => {
    setName(organization?.name)
    getSnoozedReview()
  }, [])

  /**
   * This useEffect hook checks if the dataSnoozedReview has been updated.
   * If the snoozeReviewUntil date is in the future, setShowReview is set to false.
   * Otherwise, shoe the modal
   */
  useEffect(() => {
    if (dataSnoozedReview) {
      const { snoozeReviewUntil } = dataSnoozedReview
      const snoozeShown = reactLocalStorage.get('snoozeShown')
      if (
        (snoozeReviewUntil && moment(snoozeReviewUntil).isAfter(moment())) ||
        snoozeShown
      ) {
        setShowReview(false)
      } else {
        checkPermission(['canManageMemberScholarships']) && setShowReview(true)
      }
    }
  }, [dataSnoozedReview, setShowReview])

  const isIndividual = organization?.name === INDIVIDUAL_ORGANIZATION

  const unavailable =
    errorDetail || (!loadingDetail && !dataDetail?.detail) || isIndividual

  const handleOnSnooze = (e) => {
    const snoozeReview = e.target.checked
    const data = { snoozeReview }
    saveSnoozedReview({ data })
  }

  const handleOnShow = () => {
    // add snoozed to reactLocalStorage 'snoozeShown'
    reactLocalStorage.set('snoozeShown', true)
  }

  const aggregatedData = React.useMemo(() => {
    if (!selectedLocation || !data) return null

    const { value } = selectedLocation
    let filteredData = []

    if (value === 'NA') {
      // Sum all data for North America
      filteredData = data
    } else if (value.includes('-')) {
      // Specific state selected (e.g., 'US-AL')
      const [country, state] = value.split('-')
      filteredData = data.filter(
        (item) => item.countryCode === country && item.stateCode === state
      )
    } else {
      // Country selected (e.g., 'US' or 'CA')
      filteredData = data.filter((item) => item.countryCode === value)
    }

    // Aggregate the data
    return {
      awardAmount: sumBy(filteredData, 'awardAmount'),
      awardCount: sumBy(filteredData, (item) => parseInt(item.awardCount) || 0),
      programCount: sumBy(
        filteredData,
        (item) => parseInt(item.programCount) || 0
      ),
      totalOrgCnt: sumBy(
        filteredData,
        (item) => parseInt(item.totalOrgCnt) || 0
      ),
      mbrCnt: sumBy(filteredData, (item) => parseInt(item.mbrCnt) || 0),
      nonMbrCnt: sumBy(filteredData, (item) => parseInt(item.nonMbrCnt) || 0),
      minAward:
        filteredData.length > 0
          ? Math.min(...filteredData.map((item) => item.minAward))
          : 0,
      maxAward:
        filteredData.length > 0
          ? Math.max(...filteredData.map((item) => item.maxAward))
          : 0,
    }
  }, [selectedLocation, data])

  return (
    <>
      <HomeImage />
      <DashboardStats
        translate={translate}
        locationOptions={locationOptions}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        loading={loading}
        aggregatedData={aggregatedData}
      />
      <Divider />
      <Row>
        <Col md={4}>
          <Header>{translate('landing_partner')}</Header>
          <PartnerSpotlight link={<CustomLinks location="PARTNER.LINK" />} />
        </Col>
        <Col md={4}>
          <Header>{translate('landing_highlights')}</Header>
          <Events>
            <ul>
              <li>
                <FaSearch />
                <Link href="/search/advanced/organizations">
                  {translate('breadcrumbs_member_org_search_text')}
                </Link>{' '}
              </li>
              <li>
                <BsBarChartFill />
                {unavailable ? (
                  <span>{translate('peer_report_title')}</span>
                ) : (
                  <Link href="/comparison/peer">
                    {translate('peer_report_title')}
                  </Link>
                )}
              </li>
              <li>
                <BsBarChartFill />
                {unavailable ? (
                  <span> {translate('criteria_report_title')}</span>
                ) : (
                  <Link href="/comparison/criteria">
                    {translate('criteria_report_title')}
                  </Link>
                )}
              </li>
            </ul>
          </Events>
        </Col>
        <Col md={4}>
          <Header>{translate('landing_information')}</Header>
          {!isIndividual ? (
            <>
              {unavailable && <OrganizationUnavailable />}
              {loadingDetail && <DetailLoader />}
              {dataDetail?.detail && (
                <Organization detail={dataDetail?.detail} fullName={fullName} />
              )}
              <Labels>
                <ButtonLabel
                  color={theme.palette.primary[0]}
                  disabled={unavailable}
                  onClick={() => navigate('/member-detail')}
                  className="clickable"
                >
                  {translate('landing_org')}
                </ButtonLabel>
                <ButtonLabel
                  color={theme.palette.secondary[0]}
                  disabled={unavailable}
                  onClick={() => handleClick('programs')}
                  permissions={['scholarships']}
                >
                  {translate('landing_programs')}
                </ButtonLabel>
                <Permission permission={['canManageMemberScholarships']}>
                  <ButtonLabel
                    color={theme.palette.grayscale[2]}
                    onClick={() => handleClick('data-center')}
                    permissions={['canManageMemberScholarships']}
                  >
                    {translate('landing_memberdata')}
                  </ButtonLabel>
                </Permission>
              </Labels>
            </>
          ) : (
            <MemberInformation fullName={fullName} />
          )}
        </Col>
      </Row>
      <ReviewScholarshipsModal
        show={showReview}
        onHide={() => setShowReview(false)}
        translate={translate}
        onSnooze={handleOnSnooze}
        onUpdate={() => navigate('/data-center/programs')}
        onShow={handleOnShow}
      />
    </>
  )
}

const Events = styled.div`
  height: 20rem;
  overflow-y: auto;

  > ul {
    list-style: none;
    padding-left: 1rem;
    > li {
      margin-bottom: 0.7rem;
      > svg {
        color: ${({ theme }) => theme.palette.primary[0]};
        margin-right: 1rem;
      }

      > a,
      span {
        font-size: 0.9rem;
        color: ${({ theme }) => theme.palette.grayscale[1]};
      }
    }
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #DFDFDF;
  margin: 2rem 0;
`

const Labels = styled.div`
  margin: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale[1]};
`

const Header = styled.h4`
  color: ${({ theme }) => theme.palette.grayscale[1]};
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale[1]};
  margin: 1rem;
  padding-bottom: 0.6rem;
  font-weight: bold;
`

const Spinner = styled.div`
  text-align: center;
`

export default Landing
